import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { PageHeader } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ChevronLeft, ChevronRight, Plus, Flag, Film, CheckSquare, Rocket } from "lucide-react";
import { toast } from "sonner";

const TYPE_META = {
  task: { color: "#8B5CF6", icon: CheckSquare, label: "Task", route: "/tasks" },
  video: { color: "#06B6D4", icon: Film, label: "Release", route: "/videos" },
  published: { color: "#10B981", icon: Rocket, label: "Published", route: "/videos" },
  milestone: { color: "#F59E0B", icon: Flag, label: "Milestone", route: "/calendar" },
};

export default function CalendarPage() {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [cursor, setCursor] = useState(new Date());
  const [filter, setFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [dragItem, setDragItem] = useState(null);
  const [dragOverDay, setDragOverDay] = useState(null);
  const [form, setForm] = useState({ title: "", description: "", date: new Date().toISOString().slice(0, 10), type: "milestone" });

  const load = () => api.get("/calendar").then((r) => setEvents(r.data));
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => events.filter((e) => filter === "all" || e.type === filter), [events, filter]);

  const year = cursor.getFullYear();
  const month = cursor.getMonth();
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const eventsOn = (day) => {
    const dateStr = new Date(year, month, day).toDateString();
    return filtered.filter((e) => new Date(e.date).toDateString() === dateStr);
  };

  const addMilestone = async () => {
    if (!form.title.trim()) return toast.error("Title required");
    await api.post("/milestones", form);
    toast.success("Milestone added");
    setOpen(false);
    setForm({ title: "", description: "", date: new Date().toISOString().slice(0, 10), type: "milestone" });
    load();
  };

  const openEvent = (e) => {
    if (e.type === "milestone") setSelected(e);
    else if (e.type === "task") navigate(`/tasks?open=${e.id}`);
    else if (e.type === "video" || e.type === "published") navigate(`/videos?open=${e.id}`);
  };

  const reschedule = async (ev, day) => {
    const pad = (n) => String(n).padStart(2, "0");
    const iso = `${year}-${pad(month + 1)}-${pad(day)}`;
    if (ev.date && ev.date.slice(0, 10) === iso) return;
    try {
      if (ev.type === "video") await api.put(`/videos/${ev.id}`, { release_date: iso });
      else if (ev.type === "published") await api.put(`/videos/${ev.id}`, { published_date: iso });
      else if (ev.type === "task") await api.put(`/tasks/${ev.id}`, { due_date: iso });
      else if (ev.type === "milestone") await api.patch(`/milestones/${ev.id}`, { date: iso });
      toast.success(`Moved "${ev.title}" to ${iso}`);
      load();
    } catch (e) {
      toast.error("Could not reschedule");
    }
  };

  const todayStr = new Date().toDateString();
  const monthName = cursor.toLocaleDateString(undefined, { month: "long", year: "numeric" });

  return (
    <div className="p-6 lg:p-10 max-w-7xl mx-auto animate-fade-up" data-testid="calendar-container">
      <PageHeader title="Calendar" subtitle="Releases, milestones and task deadlines — drag any item to reschedule.">
        <Button onClick={() => setOpen(true)} data-testid="add-milestone-button" className="font-semibold">
          <Plus className="w-4 h-4 mr-1.5" /> Add Milestone
        </Button>
      </PageHeader>

      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <Button variant="outline" size="icon" onClick={() => setCursor(new Date(year, month - 1, 1))} data-testid="calendar-prev-month"><ChevronLeft className="w-4 h-4" /></Button>
          <h2 className="font-display text-xl font-semibold w-48 text-center">{monthName}</h2>
          <Button variant="outline" size="icon" onClick={() => setCursor(new Date(year, month + 1, 1))} data-testid="calendar-next-month"><ChevronRight className="w-4 h-4" /></Button>
          <Button variant="ghost" size="sm" onClick={() => setCursor(new Date())}>Today</Button>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex gap-3 text-xs">
            {Object.entries(TYPE_META).map(([k, m]) => (
              <span key={k} className="flex items-center gap-1.5 text-muted-foreground"><span className="w-2.5 h-2.5 rounded-full" style={{ background: m.color }} />{k === "published" ? "Published" : `${m.label}s`}</span>
            ))}
          </div>
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-40" data-testid="calendar-filter-dropdown"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Items</SelectItem>
              <SelectItem value="video">Releases</SelectItem>
              <SelectItem value="published">Published</SelectItem>
              <SelectItem value="task">Tasks</SelectItem>
              <SelectItem value="milestone">Milestones</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="glass border border-border rounded-2xl p-4">
        <div className="grid grid-cols-7 gap-2 mb-2">
          {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
            <div key={d} className="text-center text-xs uppercase tracking-wider text-muted-foreground font-semibold py-2">{d}</div>
          ))}
        </div>
        <div className="grid grid-cols-7 gap-2">
          {cells.map((day, i) => {
            if (day === null) return <div key={`e${i}`} className="min-h-[110px]" />;
            const dayEvents = eventsOn(day);
            const isToday = new Date(year, month, day).toDateString() === todayStr;
            return (
              <div key={day}
                onDragOver={(ev) => { ev.preventDefault(); setDragOverDay(day); }}
                onDragLeave={() => setDragOverDay((d) => (d === day ? null : d))}
                onDrop={() => { if (dragItem) reschedule(dragItem, day); setDragItem(null); setDragOverDay(null); }}
                className={`min-h-[110px] rounded-xl border p-2 transition-colors ${dragOverDay === day ? "border-primary ring-2 ring-primary/50 bg-primary/10" : isToday ? "border-primary/50 bg-primary/5" : "border-border bg-secondary/20"}`}>
                <span className={`text-xs font-mono ${isToday ? "text-primary font-bold" : "text-muted-foreground"}`}>{day}</span>
                <div className="space-y-1 mt-1">
                  {dayEvents.slice(0, 3).map((e) => {
                    const m = TYPE_META[e.type];
                    return (
                      <button key={`${e.type}-${e.id}`} onClick={() => openEvent(e)} data-testid="calendar-item-event"
                        draggable
                        onDragStart={(ev) => { ev.dataTransfer.effectAllowed = "move"; setDragItem(e); }}
                        onDragEnd={() => { setDragItem(null); setDragOverDay(null); }}
                        className="w-full text-left flex items-center gap-1 px-1.5 py-1 rounded-md text-[11px] font-medium truncate hover:brightness-125 transition-all cursor-grab active:cursor-grabbing"
                        style={{ background: `${m.color}22`, color: m.color }}>
                        <m.icon className="w-2.5 h-2.5 shrink-0" />
                        <span className="truncate">{e.title}</span>
                      </button>
                    );
                  })}
                  {dayEvents.length > 3 && <span className="text-[10px] text-muted-foreground pl-1">+{dayEvents.length - 3} more</span>}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Add milestone dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="glass border-border">
          <DialogHeader><DialogTitle className="font-display">Add Milestone</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2"><Label>Title</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="milestone-title-input" placeholder="Season finale drop" /></div>
            <div className="space-y-2"><Label>Date</Label><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} /></div>
            <div className="space-y-2"><Label>Description</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
          </div>
          <DialogFooter><Button onClick={addMilestone} data-testid="milestone-submit" className="font-semibold">Add</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Milestone detail */}
      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="glass border-border">
          {selected && (
            <>
              <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><Flag className="w-5 h-5 text-primary" />{selected.title}</DialogTitle></DialogHeader>
              <Badge variant="secondary" className="w-fit">{new Date(selected.date).toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric", year: "numeric" })}</Badge>
              <DialogFooter>
                <Button variant="ghost" className="text-destructive hover:text-destructive" onClick={async () => { await api.delete(`/milestones/${selected.id}`); toast.success("Deleted"); setSelected(null); load(); }} data-testid="milestone-delete-button">Delete</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
